import { useState } from "react";
import api from "../../services/api";

const T = {
  brand: "#6366F1",
  brand2: "#4F46E5",
  green: "#10B981",
  red: "#EF4444",
  blue: "#3B82F6",
  bg: "#F8FAFC",
  card: "#FFFFFF",
  bdr: "#E2E8F0",
  bdr2: "#CBD5E1",
  t0: "#0F172A",
  t1: "#1E293B",
  t2: "#475569",
  t3: "#94A3B8",
  t4: "#CBD5E1",
};

const TABS = [
  { id: "general", label: "General", icon: "⚙" },
  { id: "profile", label: "Profile", icon: "👤" },
  { id: "ai", label: "AI & Voice", icon: "🧠" },
  { id: "notifications", label: "Notifications", icon: "🔔" },
  { id: "security", label: "Security", icon: "🔒" },
];

function Toggle({ value, onChange, color }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 42,
        height: 23,
        borderRadius: 12,
        background: value ? color || T.brand : T.bdr2,
        cursor: "pointer",
        position: "relative",
        transition: "background .3s",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 2.5,
          left: value ? "calc(100% - 20px)" : "3px",
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "white",
          transition: "left .3s cubic-bezier(.34,1.56,.64,1)",
          boxShadow: "0 1px 4px rgba(0,0,0,.2)",
        }}
      />
    </div>
  );
}

function Row({ label, desc, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 0",
        borderBottom: "1px solid " + T.bdr + "60",
      }}
    >
      <div style={{ flex: 1, paddingRight: 24 }}>
        <div style={{ fontSize: 12, color: T.t1, fontWeight: 500 }}>{label}</div>
        {desc && <div style={{ fontSize: 10, color: T.t3, marginTop: 2 }}>{desc}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function TabContent({ tab, form, set }) {
  const Sel = ({ value, onChange, opts }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        border: "1.5px solid " + T.bdr,
        borderRadius: 8,
        padding: "7px 12px",
        fontSize: 11,
        color: T.t1,
        outline: "none",
        background: "white",
        cursor: "pointer",
      }}
    >
      {opts.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );

  if (tab === "general")
    return (
      <>
        <Row label="Theme" desc="Interface appearance">
          <Sel
            value={form.theme}
            onChange={(v) => set("theme", v)}
            opts={[
              ["light", "Light"],
              ["dark", "Dark"],
              ["system", "System"],
            ]}
          />
        </Row>
        <Row label="Interface Language">
          <Sel
            value={form.language}
            onChange={(v) => set("language", v)}
            opts={[
              ["en", "English"],
              ["es", "Spanish"],
            ]}
          />
        </Row>
        <Row label="Interview Language Default" desc="Default language for new positions">
          <Sel
            value={form.interviewDefaultLang}
            onChange={(v) => set("interviewDefaultLang", v)}
            opts={[
              ["en", "English"],
              ["es", "Spanish (Bilingual)"],
            ]}
          />
        </Row>
      </>
    );

  if (tab === "profile") {
    const initial = (form.name || "A").trim().charAt(0).toUpperCase() || "A";
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 20,
            padding: "14px 16px",
            background: T.bg,
            borderRadius: 12,
            border: "1px solid " + T.bdr,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: `linear-gradient(135deg,${T.brand},${T.brand2})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              fontWeight: 700,
              color: "white",
            }}
          >
            {initial}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.t0 }}>{form.name}</div>
            <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{form.email}</div>
          </div>
        </div>
        {[
          ["Full Name", "name"],
          ["Email", "email"],
        ].map(([l, k]) => (
          <Row key={k} label={l}>
            <input
              value={form[k]}
              onChange={(e) => set(k, e.target.value)}
              style={{
                border: "1.5px solid " + T.bdr,
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 11,
                color: T.t1,
                outline: "none",
                width: 200,
              }}
            />
          </Row>
        ))}
      </>
    );
  }

  if (tab === "ai")
    return (
      <>
        <Row label="LLM Provider" desc="Free Groq tier used by default">
          <Sel
            value={form.aiModel}
            onChange={(v) => set("aiModel", v)}
            opts={[
              ["groq", "Groq — Llama 3.3 70B (Free)"],
              ["openai", "OpenAI GPT-3.5 (Fallback)"],
            ]}
          />
        </Row>
        <Row label="AI Confidence Threshold" desc="Min confidence to accept AI output">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="range"
              min={50}
              max={99}
              value={form.aiConfidence}
              onChange={(e) => set("aiConfidence", +e.target.value)}
              style={{ width: 130, accentColor: T.brand }}
            />
            <span style={{ fontSize: 14, fontWeight: 800, color: T.brand, minWidth: 40 }}>
              {form.aiConfidence}%
            </span>
          </div>
        </Row>
        <Row label="TTS Engine" desc="Text-to-speech for question audio">
          <Sel
            value={form.ttsEngine}
            onChange={(v) => set("ttsEngine", v)}
            opts={[
              ["edge-tts", "edge-tts — Microsoft Neural (Free)"],
              ["elevenlabs", "ElevenLabs (Voice Clone)"],
            ]}
          />
        </Row>
        <Row label="Voice Gender">
          <Sel
            value={form.ttsVoice}
            onChange={(v) => set("ttsVoice", v)}
            opts={[
              ["female", "Female — Aria Neural"],
              ["male", "Male — Guy Neural"],
            ]}
          />
        </Row>
        <Row label="Auto-Heal failing tests">
          <Toggle value={form.autoHeal} onChange={(v) => set("autoHeal", v)} />
        </Row>
      </>
    );

  if (tab === "notifications")
    return (
      <>
        <Row label="Email alerts">
          <Toggle value={form.emailNotif} onChange={(v) => set("emailNotif", v)} />
        </Row>
        <Row label="Interview completed">
          <Toggle value={form.runNotif} onChange={(v) => set("runNotif", v)} color={T.green} />
        </Row>
        <Row label="AI evaluation ready">
          <Toggle value={form.healNotif} onChange={(v) => set("healNotif", v)} color={T.blue} />
        </Row>
      </>
    );

  if (tab === "security")
    return (
      <>
        <Row label="Two-factor authentication">
          <Toggle value={form.twofa} onChange={(v) => set("twofa", v)} color={T.green} />
        </Row>
        <Row label="Session timeout">
          <Sel
            value={form.sessionTimeout}
            onChange={(v) => set("sessionTimeout", v)}
            opts={[
              ["60", "1 hour"],
              ["240", "4 hours"],
              ["480", "8 hours"],
              ["0", "Never"],
            ]}
          />
        </Row>
        <Row label="API Key" desc="Use to authenticate API requests">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <code
              style={{
                fontSize: 10,
                color: T.green,
                background: T.bg,
                padding: "6px 12px",
                borderRadius: 7,
                border: "1px solid " + T.bdr,
                fontFamily: "monospace",
              }}
            >
              pai_sk_••••••••••••••••
            </code>
            <button
              type="button"
              style={{
                background: "transparent",
                border: "1.5px solid " + T.bdr,
                color: T.t2,
                fontSize: 11,
                padding: "5px 13px",
                borderRadius: 8,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Copy
            </button>
          </div>
        </Row>
      </>
    );

  return null;
}

export default function Settings() {
  const [tab, setTab] = useState("general");
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: "Admin",
    email: "admin@wizetok.app",
    twofa: true,
    emailNotif: true,
    healNotif: true,
    runNotif: true,
    language: "en",
    theme: "light",
    interviewDefaultLang: "en",
    aiModel: "groq",
    aiConfidence: 75,
    autoHeal: true,
    ttsVoice: "female",
    ttsEngine: "edge-tts",
    sessionTimeout: "480",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (tab === "profile") {
      try {
        // TODO: add PUT /api/v1/admin/profile (or extend /api/v1/auth/me) on backend when available
        await api.put("/api/v1/admin/profile", {
          name: form.name,
          email: form.email,
        });
      } catch (err) {
        console.error("Profile save failed:", err);
      }
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
      <div
        style={{
          width: 200,
          background: T.card,
          borderRight: "1px solid " + T.bdr,
          padding: "16px 10px",
        }}
      >
        <div
          style={{
            fontSize: 9,
            color: T.t4,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            fontWeight: 700,
            padding: "4px 8px 12px",
          }}
        >
          Settings
        </div>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "9px 10px",
                borderRadius: 9,
                marginBottom: 2,
                background: active ? T.brand + "10" : "none",
                border: "none",
                color: active ? T.brand : T.t2,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                textAlign: "left",
                transition: "all .17s",
                borderLeft: "3px solid " + (active ? T.brand : "transparent"),
              }}
            >
              <span style={{ fontSize: 14, minWidth: 18, textAlign: "center" }}>{t.icon}</span>
              {t.label}
              {active && (
                <span
                  style={{
                    marginLeft: "auto",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: T.brand,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px", background: T.bg }}>
        <div style={{ maxWidth: 600 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: T.t0 }}>
              {TABS.find((t) => t.id === tab)?.label}
            </div>
            <button
              type="button"
              onClick={handleSave}
              style={{
                background: saved ? T.green : T.brand,
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {saved ? "✓ Saved" : "Save Changes"}
            </button>
          </div>
          <div
            style={{
              background: T.card,
              border: "1px solid " + T.bdr,
              borderRadius: 14,
              padding: "20px 24px",
              boxShadow: "0 1px 4px rgba(0,0,0,.05)",
            }}
          >
            <TabContent tab={tab} form={form} set={set} />
          </div>
        </div>
      </div>
    </div>
  );
}
